import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { EmptyState, Loading, ModuleHero, Notice, StatusPill, Tabs } from '../../components/ui';
import { COMPANY_STATUS, REPORT_STATUS, gradeTone, timeAgo } from '../../lib/format';
import { CARD, CARD_STYLE, FIELD, FIELD_STYLE } from '../../lib/ui';
import type { Company, ReportStatus } from '../../types/workspace';
import CompanyLogo from '../../components/CompanyLogo';

type Row = Company & {
  ratings: { version: number; grade: string; score: number; status: ReportStatus }[];
  _count: { documents: number; remediations: number };
};

type Filter = 'all' | 'intake' | 'review' | 'rated' | 'outreach';

const FILTERS: Record<Filter, string[] | null> = {
  all: null,
  intake: ['INVITED', 'INTAKE'],
  review: ['SUBMITTED', 'REANALYSIS'],
  rated: ['RATED', 'REMEDIATING'],
  outreach: ['READY', 'OUTREACH', 'FUNDED'],
};

export default function AdminCompanies({ scope }: { scope: 'all' | 'mine' }) {
  const { data, loading, error } = useApi<{ companies: Row[] }>(`/admin/companies?scope=${scope === 'mine' ? 'assigned' : 'all'}`);
  const [filter, setFilter] = useState<Filter>('all');
  const [query, setQuery] = useState('');

  const companies = useMemo(() => data?.companies || [], [data]);
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return companies.filter((c) => {
      const statuses = FILTERS[filter];
      if (statuses && !statuses.includes(c.status)) return false;
      if (!q) return true;
      return [c.logicalName, c.name, c.email, c.countryCode, c.sector, c.customer?.name, c.workspaceLabel, c.classification?.type.label].some((v) => v?.toLowerCase().includes(q));
    });
  }, [companies, filter, query]);

  const count = (f: Filter) => (FILTERS[f] ? companies.filter((c) => FILTERS[f]!.includes(c.status)).length : companies.length);

  return (
    <div>
      <ModuleHero
        eyebrow={scope === 'mine' ? 'Assigned to you' : 'Portfolio'}
        title={scope === 'mine' ? 'My companies' : 'All companies'}
        description={
          scope === 'mine'
            ? 'Companies you are responsible for — review documents, run ratings and take them to partners.'
            : 'Every company on Micus, across all analysts. Open any company to see its full workspace.'
        }
      />

      <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
        <Tabs
          value={filter}
          onChange={setFilter}
          options={[
            { id: 'all', label: 'All', count: count('all') },
            { id: 'intake', label: 'Intake', count: count('intake') },
            { id: 'review', label: 'Needs rating', count: count('review') },
            { id: 'rated', label: 'Rated', count: count('rated') },
            { id: 'outreach', label: 'Ready & outreach', count: count('outreach') },
          ]}
        />
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name, email, country…" className={`${FIELD} max-w-xs`} style={FIELD_STYLE} />
      </div>

      <div className="mt-5">
        {loading && <Loading />}
        {error && <Notice tone="rose">{error}</Notice>}
        {!loading && !error && visible.length === 0 && (
          <EmptyState
            title={companies.length ? 'No companies match' : scope === 'mine' ? 'Nothing assigned yet' : 'No companies yet'}
            body={companies.length ? 'Try another filter or search.' : 'Companies are auto-assigned to the analyst with the lightest load when they submit documents.'}
          />
        )}
        {visible.length > 0 && (
          <div className={`${CARD} overflow-hidden`} style={CARD_STYLE}>
            <div className="hidden grid-cols-[2fr_1fr_0.8fr_0.8fr_0.8fr_1.2fr] gap-3 border-b border-[#fce7f3] bg-[#fffafc] px-5 py-2.5 text-[10px] font-bold uppercase tracking-wider text-[#c084a0] md:grid">
              <span>Company</span>
              <span>Status</span>
              <span>Documents</span>
              <span>Remediation</span>
              <span>Rating</span>
              <span>Assigned analyst</span>
            </div>
            <ul className="divide-y divide-[#fce7f3]">
              {visible.map((c) => {
                const s = COMPANY_STATUS[c.status] || { label: c.status, tone: 'slate' as const };
                const r = c.ratings[0];
                return (
                  <li key={c.id}>
                    <Link to={`/companies/${c.id}`} className="grid gap-2 px-5 py-3.5 transition-colors hover:bg-[#fff5f8] md:grid-cols-[2fr_1fr_0.8fr_0.8fr_0.8fr_1.2fr] md:items-center md:gap-3">
                      <div className="flex min-w-0 items-center gap-2.5">
                        <CompanyLogo companyId={c.id} name={c.logicalName || c.name} hasLogo={c.hasLogo} className="h-9 w-9" />
                        <div className="min-w-0">
                        <p className="truncate text-sm font-bold text-[#6b0030]">
                          {c.logicalName || c.name}
                          {c.workspaceLabel && <span className="font-normal text-[#c084a0]"> · {c.workspaceLabel}</span>}
                        </p>
                        <p className="truncate text-[11px] text-[#c084a0]">
                          {c.countryCode} · {c.classification?.type.label || 'type not set'}
                          {c.sector ? ` · ${c.sector}` : ''} · updated {timeAgo(c.updatedAt)}
                        </p>
                        </div>
                      </div>
                      <span>
                        <StatusPill label={s.label} tone={s.tone} />
                      </span>
                      <span className="text-sm text-[#6b0030]">{c._count.documents} files</span>
                      <span className={`text-sm ${c._count.remediations ? 'font-bold text-[#b45309]' : 'text-[#c084a0]'}`}>{c._count.remediations} open</span>
                      <span className="flex flex-col items-start gap-1">
                        {r ? (
                          <>
                            <StatusPill label={`${r.grade} · ${r.score}`} tone={gradeTone(r.score)} />
                            {REPORT_STATUS[r.status] && <span className="text-[10px] font-semibold text-[#b0708f]">{REPORT_STATUS[r.status].label}</span>}
                          </>
                        ) : (
                          <span className="text-xs text-[#c084a0]">—</span>
                        )}
                      </span>
                      <span className="truncate text-xs text-[#6b3a52]">{c.assignedAdmin ? c.assignedAdmin.name || c.assignedAdmin.email : 'Unassigned'}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
